import { createTuyau } from '@tuyau/core/client'
import type { TuyauError } from '@tuyau/core/client'
import { registry } from '../../../server/.adonisjs/client/registry'
import type { Registry } from '../../../server/.adonisjs/client/registry/schema'
import { QueryClient } from '@tanstack/react-query'
import { createTuyauReactQueryClient } from '@tuyau/react-query'
import { getAuth } from 'firebase/auth'
import type { RepositoryApiClient } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'
import type {
  FieldValues,
  Path,
  UseFormSetError,
  UseFormSetFocus,
} from 'react-hook-form'
import { z } from 'zod'

// I'm leaving this in, but you should use `api` or `query` instead of `tuyau` directly in your components/hooks.
// This is just for advanced use cases where you need to access the underlying Tuyau client
export const tuyau = createTuyau({
  registry: registry,
  baseUrl:
    import.meta.env.VITE_SERVER_URL ??
    (import.meta.env.DEV ? 'http://localhost:3334' : window.location.origin),
  hooks: {
    beforeRequest: [
      async (request) => {
        const user = getAuth().currentUser
        if (user) {
          const token = await user.getIdToken()
          request.headers.set('Authorization', `Bearer ${token}`)
        }
        // e2e points each spec file at its own named Firestore database
        // (FirebaseRepository.build reads the same key). Tell the server which
        // one, or its Firestore reads hit `(default)` and see none of the
        // seeded data. Unset in normal use, and the server only honors the
        // header when APP_ENV is e2e.
        const databaseId = localStorage.getItem('firestore-databaseId')
        if (databaseId) request.headers.set('x-firestore-database', databaseId)
      },
    ],
  },
})

export const api = tuyau.api.api

// The Tuyau client, injected into FirebaseRepository so the cubits can call the
// Adonis server (e.g. the student assignment + meeting slide-deck reads behind
// the useV2SlideDeck*Read flags) instead of the cloud functions. Typed via the
// repo's RepositoryApiClient facade — a structural match, so no cast. The
// databaseId the cloud function takes is sent by the `x-firestore-database`
// header (beforeRequest hook above), so it isn't a param here.
export const repositoryApi: RepositoryApiClient = api

/**
 * A route's success response, as the controller returns it. Use this instead of
 * hand-writing a matching type, which drifts as soon as the server adds a field.
 */
export type ApiResponse<Name extends keyof Registry> =
  Registry[Name]['types']['response']

/** A route's accepted request body, as its validator defines it. */
export type ApiBody<Name extends keyof Registry> =
  Registry[Name]['types']['body']

export const queryClient = new QueryClient()
export const query = createTuyauReactQueryClient({ client: tuyau })

const errorSchema = z.object({
  field: z.string(),
  message: z.string(),
  meta: z
    .object({
      min: z.number().optional(),
    })
    .optional(),
  rule: z.string().optional(),
})

const errorsSchema = z.object({
  errors: z.array(errorSchema),
})

type ServerFieldError = z.infer<typeof errorSchema>

/**
 * Maps a Tuyau 422 response onto react-hook-form field errors.
 * Returns true if errors were applied (caller should return early).
 *
 * Pass `getMessage` to override the displayed message based on the error rule/field.
 * Return `undefined` to fall back to the server-provided message.
 */
export function applyServerErrors<T extends FieldValues>(
  error: TuyauError | null | undefined,
  setError: UseFormSetError<T>,
  options?: {
    setFocus?: UseFormSetFocus<T>
    fieldOrder?: readonly Path<T>[]
    getMessage?: (err: ServerFieldError) => string | undefined
    throw?: boolean
  }
): boolean {
  if (!error) return false

  if (!error?.isValidationError()) {
    if (options?.throw) throw error
    return false
  }

  const parsed = errorsSchema.safeParse(error.response)
  const errors = parsed.success ? parsed.data.errors : undefined
  if (!errors) {
    if (options?.throw) throw error
    return false
  }

  for (const err of errors) {
    const message = options?.getMessage?.(err) ?? err.message
    setError(err.field as Path<T>, { message })
  }

  if (options?.setFocus && options?.fieldOrder) {
    const first = options.fieldOrder.find((f) =>
      errors.some((e) => e.field === f)
    )
    if (first) options.setFocus(first)
  }

  return true
}
